import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import type { Database } from "@/integrations/supabase/types";

type Forecast = Database["public"]["Tables"]["sentinel_forecasts"]["Row"];

interface ForecastChartProps {
  forecasts: Forecast[];
}

export function ForecastChart({ forecasts }: ForecastChartProps) {
  if (forecasts.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center">
        <TrendingUp className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Nenhum forecast disponível. Execute o agente Forecast.</p>
      </CardContent></Card>
    );
  }

  // Group by metric
  const byMetric: Record<string, Forecast[]> = {};
  for (const f of forecasts) {
    const key = f.metric;
    if (!byMetric[key]) byMetric[key] = [];
    byMetric[key].push(f);
  }

  const metricLabels: Record<string, string> = {
    msg_volume_daily: "Volume de Mensagens (diário)",
    cost_next_month: "Custo Projetado (mensal)",
    ticket_volume_daily: "Volume de Tickets",
    overall_score: "Score Geral",
  };

  return (
    <div className="space-y-4">
      {Object.entries(byMetric).map(([metric, items]) => {
        const latest = items[0];
        const predictions = (latest.predictions as Array<{ day: number; value: number }>) || [];
        const band = (latest.confidence_band as Record<string, number>) || {};
        const chartData = predictions.map(p => ({
          label: `+${p.day}d`,
          value: p.value,
          lower: band.lower ?? p.value * 0.8,
          upper: band.upper ?? p.value * 1.2,
        }));

        return (
          <Card key={metric}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">{metricLabels[metric] || metric}</CardTitle>
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-xs">
                    Confiança: {((latest.confidence ?? 0) * 100).toFixed(0)}%
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    Previsão: {Number(latest.predicted_value).toFixed(1)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                    <Area type="monotone" dataKey="upper" stroke="none" fill="hsl(var(--primary))" fillOpacity={0.05} />
                    <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} strokeWidth={2} name="Previsão" />
                    <Area type="monotone" dataKey="lower" stroke="none" fill="hsl(var(--primary))" fillOpacity={0.05} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
                  Dados insuficientes para gráfico. Valor previsto: {Number(latest.predicted_value).toFixed(1)}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Método: {latest.method || "-"} · Dimensão: {latest.dimension || "-"} · Horizonte: {latest.horizon_days}d
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
